/* eslint-disable react/no-unescaped-entities */
import React, { useEffect } from "react";
import {
    ArrowRight, ArrowLeft, Sparkles, Download, Printer, Mail, MessageCircle,
    Building2, ShoppingBag, Factory, Heart, Briefcase, Layers, Database,
    Brain, Shield, Clock, IndianRupee, Lock, Calendar, Zap, Eye,
} from "lucide-react";
import { PRODUCTS } from "../lib/products";
import "../styles/launch-deck.css";

/**
 * /launch-deck — A pre-launch, closed-circle pitch deck.
 *
 * Pre-launch concept: not for the public site index, not in the nav.
 * The page is no-indexed and unlisted in sitemap.xml. Designed to be
 * read slide-by-slide on a laptop, or printed to PDF as a one-slide-per-page
 * deck via the browser's "Print" dialog.
 *
 * Voice: Notion-warm + Scitus-restrained. First-person where it fits.
 * No fake traction. No padded metrics. Conviction is the proof point.
 */

const Slide = ({ id, eyebrow, children, kicker, footerNote, accent = false, full = false }) => (
    <section
        className={`ld-slide${accent ? " ld-slide-accent" : ""}${full ? " ld-slide-full" : ""}`}
        id={id}
        data-testid={`slide-${id}`}
    >
        <div className="ld-slide-inner">
            {eyebrow && <span className="ld-eyebrow" data-testid={`eyebrow-${id}`}>{eyebrow}</span>}
            <div className="ld-body">{children}</div>
            {kicker && <p className="ld-kicker" data-testid={`kicker-${id}`}>{kicker}</p>}
        </div>
        <div className="ld-footer">
            <span className="ld-footer-brand">scitus.ai</span>
            <span className="ld-footer-note">{footerNote || "Pre-launch · For the closed circle"}</span>
            <span className="ld-footer-page">{id}</span>
        </div>
    </section>
);

// Helpers — small, reusable display pieces
const Stat = ({ value, label }) => (
    <div className="ld-stat">
        <span className="ld-stat-value">{value}</span>
        <span className="ld-stat-label">{label}</span>
    </div>
);

const ProductCard = ({ p }) => {
    // Wide lockups become illegible inside the 72×72 deck tile — prefer the
    // square mark when the product ships one.
    const src = p.logo?.compactSrc || p.logo?.src;
    const hasLogo = !!src;
    return (
        <div className="ld-product" data-testid={`ld-product-${p.slug}`}>
            <div className={`ld-product-logo${p.logo?.needsLightTile ? " ld-tile" : ""}`}>
                {hasLogo ? <img src={src} alt={`${p.name} logo`} /> : <span>{p.name[0]}</span>}
            </div>
            <div className="ld-product-meta">
                <h4>{p.name}</h4>
                <p>{p.tagline}</p>
            </div>
        </div>
    );
};

const LaunchDeckPage = () => {
    useEffect(() => {
        // Closed-circle: keep this off public indexes even if someone scrapes
        document.title = "Scitus.ai — Launch Deck (Confidential)";
        const meta = document.createElement("meta");
        meta.name = "robots";
        meta.content = "noindex, nofollow";
        meta.setAttribute("data-launch-deck", "1");
        document.head.appendChild(meta);
        return () => {
            document.querySelectorAll('meta[data-launch-deck="1"]').forEach((el) => el.remove());
        };
    }, []);

    const handlePrint = () => window.print();

    return (
        <main className="launch-deck-page" data-testid="launch-deck-page">
            {/* Floating toolbar — hidden when printing */}
            <div className="ld-toolbar no-print" data-testid="ld-toolbar">
                <span className="ld-toolbar-brand">
                    <Sparkles size={14} /> Scitus.ai · Launch Deck
                </span>
                <div className="ld-toolbar-actions">
                    <a
                        href="/scitus-logo-kit.zip"
                        className="ld-tb-btn"
                        download
                        data-testid="download-logo-kit"
                    >
                        <Download size={14} /> Logo kit
                    </a>
                    <a
                        href="/scitus-launch-deck.pdf"
                        className="ld-tb-btn"
                        download
                        data-testid="download-pdf"
                    >
                        <Download size={14} /> PDF
                    </a>
                    <button type="button" className="ld-tb-btn ld-tb-btn-primary" onClick={handlePrint} data-testid="print-deck">
                        <Printer size={14} /> Print
                    </button>
                </div>
            </div>

            {/* ─── Slide 01 — The letter ─────────────────────────────────────── */}
            <Slide id="01" eyebrow="A note before you turn the page" full>
                <h1 className="ld-h1-letter">
                    If you're reading this,<br />
                    your opinion matters to us.
                </h1>
                <div className="ld-letter">
                    <p>
                        Scitus.ai isn't public yet. We're a few weeks out from launch and
                        we wanted you to see it before the world does — partly so we can
                        fix what's broken, partly so you can tell us where we're wrong,
                        and partly because the next chapter is more fun with you in it.
                    </p>
                    <p>
                        What follows is the deck we'll show operators, advisors, and a
                        small handful of early investors when we open the doors. There's
                        no padded traction in here. No customer logos we don't have. No
                        round we've already closed. Just the product we built, the bet
                        we're making, and a small ask at the end.
                    </p>
                    <p className="ld-letter-sign">— The Scitus team · Chennai &amp; Austin</p>
                </div>
            </Slide>

            {/* ─── Slide 02 — The hook ───────────────────────────────────────── */}
            <Slide id="02" full>
                <h2 className="ld-megaline" data-testid="ld-megaline">
                    Your business runs<br />
                    on seven systems.<br />
                    <span className="ld-accent">Your decisions shouldn't.</span>
                </h2>
            </Slide>

            {/* ─── Slide 03 — How we got here ─────────────────────────────────── */}
            <Slide id="03" eyebrow="How we got here">
                <h2 className="ld-h2">
                    The night the spreadsheet broke.
                </h2>
                <div className="ld-prose">
                    <p>
                        A friend who runs a multi-format retail business called us at 11pm
                        on a Tuesday. The category review was at 9am. The numbers from her
                        ERP didn't match the numbers from her BI dashboard, which didn't
                        match the numbers in the spreadsheet her analyst had built. Three
                        sources of truth. None of them true.
                    </p>
                    <p>
                        We spent six hours reconciling. By morning we had the right
                        numbers and a much bigger question: how is it that in 2025, every
                        operator we know is still running their business on five to seven
                        disconnected tools and a thicket of spreadsheets nobody can audit?
                    </p>
                    <p>
                        The category we kept looking for didn't exist. So we built it.
                    </p>
                </div>
            </Slide>

            {/* ─── Slide 04 — The three-tier insight ──────────────────────────── */}
            <Slide id="04" eyebrow="The insight">
                <h2 className="ld-h2">
                    ERP records.<br />
                    BI explains.<br />
                    <span className="ld-accent">Something needs to decide.</span>
                </h2>
                <div className="ld-tier-grid">
                    <div className="ld-tier">
                        <span className="ld-tier-eyebrow">Legacy ERP</span>
                        <h3>Records</h3>
                        <p>Captures transactions. Stores history. Tells you what happened.</p>
                    </div>
                    <div className="ld-tier">
                        <span className="ld-tier-eyebrow">BI tools</span>
                        <h3>Explains</h3>
                        <p>Visualises the data. Builds reports. Tells you how you performed.</p>
                    </div>
                    <div className="ld-tier ld-tier-accent">
                        <span className="ld-tier-eyebrow">Scitus.ai</span>
                        <h3>Decides</h3>
                        <p>Recommends actions. Governs approvals. Tells you what to do next.</p>
                    </div>
                </div>
                <p className="ld-quietnote">
                    The missing layer isn't more software. It's the intelligence between
                    your data and your decisions. That layer has a name now.
                </p>
            </Slide>

            {/* ─── Slide 05 — The reveal ──────────────────────────────────────── */}
            <Slide id="05" eyebrow="What we built" accent>
                <h2 className="ld-megaline ld-megaline-lg">
                    Scitus.ai
                </h2>
                <p className="ld-reveal-line">
                    One operating layer.<br />
                    Seven products.<br />
                    Built for operators who need answers — not more dashboards.
                </p>
                <div className="ld-reveal-stats">
                    <Stat value="7" label="Products" />
                    <Stat value="1" label="Architecture" />
                    <Stat value="7–14 days" label="To first live transaction" />
                </div>
            </Slide>

            {/* ─── Slide 06 — The seven ───────────────────────────────────────── */}
            <Slide id="06" eyebrow="The seven">
                <h2 className="ld-h2">
                    Each one solves a real problem.<br />
                    Together, they share one brain.
                </h2>
                <div className="ld-product-grid" data-testid="ld-product-grid">
                    {PRODUCTS.map((p) => <ProductCard key={p.slug} p={p} />)}
                </div>
            </Slide>

            {/* ─── Slide 07 — Architecture ────────────────────────────────────── */}
            <Slide id="07" eyebrow="One architecture">
                <h2 className="ld-h2">
                    One data layer.<br />
                    One identity.<br />
                    One AI brain.
                </h2>
                <div className="ld-arch">
                    <div className="ld-arch-layer ld-arch-output">
                        <span className="ld-arch-label">Output</span>
                        <p>Leadership decisions &amp; AI recommendations</p>
                    </div>
                    <div className="ld-arch-layer ld-arch-intel">
                        <span className="ld-arch-label">Intelligence</span>
                        <p>AI copilot, grounded in your own operational data</p>
                    </div>
                    <div className="ld-arch-layer ld-arch-core">
                        <span className="ld-arch-label">Scitus operating layer</span>
                        <p>Identity · Audit · Approvals · Data fabric</p>
                    </div>
                    <div className="ld-arch-layer ld-arch-products">
                        <span className="ld-arch-label">Products</span>
                        <p>Finance · Projects · Procurement · HR · Signing · Analytics</p>
                    </div>
                    <div className="ld-arch-layer ld-arch-foundation">
                        <span className="ld-arch-label">Foundation</span>
                        <p>Your existing ERP, files, APIs, spreadsheets</p>
                    </div>
                </div>
                <p className="ld-quietnote">
                    The second product feels like another tab. Not another vendor.
                </p>
            </Slide>

            {/* ─── Slide 08 — AI copilot ──────────────────────────────────────── */}
            <Slide id="08" eyebrow="AI copilot · every product">
                <h2 className="ld-h2">
                    Not a chatbot.<br />
                    <span className="ld-accent">A decision engine.</span>
                </h2>
                <div className="ld-demo">
                    <div className="ld-demo-prompt">
                        <span className="ld-demo-url">app.scitus.ai / ask</span>
                        <p>Why did our margins decline this quarter?</p>
                    </div>
                    <div className="ld-demo-answer">
                        <span className="ld-demo-tag">Scitus analysis · grounded in your data</span>
                        <ul>
                            <li><strong>Procurement costs up 4.2%</strong> — three vendors breached price benchmarks in August, no renegotiation triggered.</li>
                            <li><strong>₹8.2 Cr overdue receivables</strong> — 23 accounts past 60-day SLA, collections team not flagged.</li>
                            <li><strong>South region discounting rose 8.3%</strong> — approved outside standard workflow, no audit trail.</li>
                        </ul>
                        <div className="ld-demo-rec">
                            <Sparkles size={14} /> Recommended: trigger vendor renegotiation in Kretara · enforce discount approval gate.
                        </div>
                    </div>
                </div>
                <p className="ld-quietnote">
                    Plain language in. Decisions out. Included on every product —
                    not a separate SKU.
                </p>
            </Slide>

            {/* ─── Slide 09 — Industries ──────────────────────────────────────── */}
            <Slide id="09" eyebrow="Industries we know in the bones">
                <h2 className="ld-h2">
                    We didn't generalise.<br />
                    We picked five and went deep.
                </h2>
                <div className="ld-industries">
                    {[
                        { icon: ShoppingBag, name: "Retail", line: "Turn every SKU into one revenue story.", points: ["Sales dashboard across stores", "Inventory ageing & turnover", "Vendor scorecards"] },
                        { icon: Building2, name: "Real Estate", line: "Run every tower on one source of truth.", points: ["Bookings & collections tracker", "Inventory absorption", "Project cost control"] },
                        { icon: Factory, name: "Manufacturing", line: "Know what's producing, delaying, costing.", points: ["Production tracking", "Procurement intelligence", "Cost variance"] },
                        { icon: Heart, name: "Healthcare", line: "Connect revenue, workforce & operations.", points: ["Revenue cycle visibility", "Denial tracking", "Provider productivity"] },
                        { icon: Briefcase, name: "Services", line: "Utilisation, delivery & profitability — live.", points: ["Sales pipeline & conversion", "Utilization & billable hours", "Receivables tracking"] },
                    ].map((ind) => (
                        <div className="ld-industry" key={ind.name}>
                            <div className="ld-industry-head">
                                <span className="ld-industry-icon"><ind.icon size={16} /></span>
                                <h4>{ind.name}</h4>
                            </div>
                            <p className="ld-industry-line">{ind.line}</p>
                            <ul>{ind.points.map((pt) => <li key={pt}>{pt}</li>)}</ul>
                        </div>
                    ))}
                </div>
            </Slide>

            {/* ─── Slide 10 — Sits on top ─────────────────────────────────────── */}
            <Slide id="10" eyebrow="A design choice we'll defend">
                <h2 className="ld-h2">
                    We don't replace your ERP.<br />
                    <span className="ld-accent">We sit on top of it.</span>
                </h2>
                <div className="ld-prose">
                    <p>
                        Tally, SAP, Oracle, NetSuite, Zoho, a custom build from 2017 — keep it.
                        We read from it, govern around it, and give your operators the
                        intelligence layer your ERP was never built to provide.
                    </p>
                    <p>
                        Every ERP replacement we've seen takes 6–18 months, an army of
                        consultants, and ends with a team that's tired of software. We
                        looked at that and said no. The work was never the ERP. The work
                        is the spreadsheet your team builds around it.
                    </p>
                    <p>
                        <strong>That's what Scitus replaces. Not the system of record —
                        the system of guesswork built around it.</strong>
                    </p>
                </div>
            </Slide>

            {/* ─── Slide 11 — Delivery timeline ───────────────────────────────── */}
            <Slide id="11" eyebrow="How we deliver">
                <h2 className="ld-h2">
                    Live in days.<br />
                    Not quarters.
                </h2>
                <div className="ld-timeline">
                    {[
                        { wk: "Week 1", title: "Discover", body: "Business assessment. Outcome alignment. Map existing data sources." },
                        { wk: "Week 2–3", title: "Connect", body: "ERP connectors. Data integration. No rip-and-replace." },
                        { wk: "Week 4", title: "Configure", body: "Dashboards, approval workflows, roles, alert thresholds." },
                        { wk: "Week 5", title: "Launch", body: "Team training. Go-live on real transactions. Hypercare." },
                        { wk: "Ongoing", title: "Scale", body: "Add products. Bundle discount auto-applies. Expand modules." },
                    ].map((step) => (
                        <div className="ld-tl-step" key={step.wk}>
                            <span className="ld-tl-wk">{step.wk}</span>
                            <h4>{step.title}</h4>
                            <p>{step.body}</p>
                        </div>
                    ))}
                </div>
                <p className="ld-quietnote">
                    Most teams are live on their first product in 7–14 days. Full suite
                    rollouts in 30–60 days.
                </p>
            </Slide>

            {/* ─── Slide 12 — Pricing ─────────────────────────────────────────── */}
            <Slide id="12" eyebrow="What we'll charge">
                <h2 className="ld-h2">
                    Transparent on day one.<br />
                    Most SaaS isn't.
                </h2>
                <div className="ld-pricing">
                    {[
                        { name: "Mativex", price: "₹9,600", unit: "/workspace/mo" },
                        { name: "Vexen", price: "₹14,400", unit: "/workspace/mo" },
                        { name: "Karya", price: "₹160", unit: "/employee/mo" },
                        { name: "Vyaahar", price: "₹8,000", unit: "/workspace/mo" },
                        { name: "Attestra", price: "₹4,800", unit: "/contract/mo" },
                        { name: "Acrium", price: "₹14,400", unit: "/active project/mo" },
                        { name: "Kretara", price: "₹9,600", unit: "/workspace/mo" },
                    ].map((c) => (
                        <div className="ld-price-card" key={c.name}>
                            <span className="ld-price-name">{c.name}</span>
                            <span className="ld-price-amt">{c.price}</span>
                            <span className="ld-price-unit">{c.unit}</span>
                        </div>
                    ))}
                </div>
                <div className="ld-pricing-rules">
                    <div><Zap size={14} /> Bundle 2+ products → <strong>25% off</strong>, auto-applied</div>
                    <div><Calendar size={14} /> Billed annually → <strong>save 20%</strong></div>
                    <div><Sparkles size={14} /> Annual commitment → <strong>implementation waived</strong></div>
                </div>
            </Slide>

            {/* ─── Slide 13 — Enterprise grade ────────────────────────────────── */}
            <Slide id="13" eyebrow="Enterprise-grade from launch">
                <h2 className="ld-h2">
                    Trust is earned before the first login.
                </h2>
                <div className="ld-trust-grid">
                    {[
                        { icon: Lock, title: "Encryption everywhere", body: "TLS 1.2+ in transit. AES-256 at rest. Customer-managed keys on Enterprise." },
                        { icon: Shield, title: "RBAC + SSO", body: "Role-based access at screen, action, row level. SAML SSO + SCIM on Enterprise." },
                        { icon: Eye, title: "Audit logs", body: "Every read, write, admin action logged. SIEM-exportable on Enterprise." },
                        { icon: Database, title: "India-first residency", body: "AWS Mumbai by default. Singapore, Frankfurt, US-East for Enterprise." },
                        { icon: Layers, title: "SOC 2 in progress", body: "Controls modelled on SOC 2 Type II. Annual attestation underway. GDPR-aligned." },
                        { icon: Clock, title: "99.9% uptime target", body: "Multi-region failover, monitored 24×7. Support from the team that builds the product." },
                    ].map((t) => (
                        <div className="ld-trust-card" key={t.title}>
                            <span className="ld-trust-icon"><t.icon size={16} /></span>
                            <h4>{t.title}</h4>
                            <p>{t.body}</p>
                        </div>
                    ))}
                </div>
            </Slide>

            {/* ─── Slide 14 — Why now ─────────────────────────────────────────── */}
            <Slide id="14" eyebrow="Why now">
                <h2 className="ld-h2">
                    Four forces are converging.<br />
                    The window is small.
                </h2>
                <div className="ld-forces">
                    <div className="ld-force">
                        <span className="ld-force-num">01</span>
                        <h4>GST complexity is rising</h4>
                        <p>e-invoicing mandates, reconciliation requirements, audit exposure. Manual compliance is untenable for any growing business.</p>
                    </div>
                    <div className="ld-force">
                        <span className="ld-force-num">02</span>
                        <h4>Real-time has become table stakes</h4>
                        <p>Investors and boards expect live dashboards — not monthly packs built from spreadsheets exported last Tuesday.</p>
                    </div>
                    <div className="ld-force">
                        <span className="ld-force-num">03</span>
                        <h4>AI is no longer a feature</h4>
                        <p>Every SaaS category is being rebuilt AI-first. Scitus was designed this way from day one — built on Emergent, not retrofitted.</p>
                    </div>
                    <div className="ld-force">
                        <span className="ld-force-num">04</span>
                        <h4>Indian SMEs are scaling globally</h4>
                        <p>Operations span India + the US in two-tab spreadsheets. The market for a unified operating layer has never been clearer.</p>
                    </div>
                </div>
            </Slide>

            {/* ─── Slide 15 — Why us ──────────────────────────────────────────── */}
            <Slide id="15" eyebrow="Why we're the ones to do this">
                <h2 className="ld-h2">
                    Built by operators.<br />
                    Not consultants.
                </h2>
                <div className="ld-prose">
                    <p>
                        Every product manager on the Scitus team has either run a real
                        business or shipped real product to ones that matter. We've sat
                        through the Monday reviews. We've built the spreadsheets we now
                        want to retire. Code is downstream of context — and the product
                        shows it.
                    </p>
                    <p>
                        We're a small team. Chennai by default, Austin when we need to be
                        closer to North-American customers. We don't have a hundred-person
                        sales motion. We have product-first conviction and an AI engine
                        we've been refining for two years.
                    </p>
                    <p>
                        We believe the operating layer will be one of the defining SaaS
                        categories of the next decade. We'd rather earn that bet
                        product-by-product than win it with marketing.
                    </p>
                </div>
            </Slide>

            {/* ─── Slide 16 — Manifesto ───────────────────────────────────────── */}
            <Slide id="16" eyebrow="What we believe" accent>
                <h2 className="ld-h2 ld-manifesto-h2">
                    Six lines.<br />
                    Bookmark the ones you disagree with.
                </h2>
                <ol className="ld-manifesto">
                    <li>The best operating system for a business <em>decides</em>. It doesn't just record or explain.</li>
                    <li>Operators don't need another dashboard. They need one less spreadsheet.</li>
                    <li>AI grounded in your own data beats a smarter model trained on someone else's.</li>
                    <li>"Sits on top of your ERP" is the most underrated design choice in enterprise SaaS.</li>
                    <li>Pricing should be public on day one. If we can't say it out loud, we shouldn't charge it.</li>
                    <li>You shouldn't talk to a chatbot when something breaks. You should talk to a builder.</li>
                </ol>
            </Slide>

            {/* ─── Slide 17 — The ask ─────────────────────────────────────────── */}
            <Slide id="17" eyebrow="What we'd love from you">
                <h2 className="ld-h2">
                    A small ask.<br />
                    Pick the one that fits.
                </h2>
                <div className="ld-ask-grid">
                    <div className="ld-ask">
                        <span className="ld-ask-num">01</span>
                        <h4>Be a design partner</h4>
                        <p>
                            Six founding operators. We co-build a connector or a workflow
                            specific to your business. First year free. You get a product
                            shaped around your reality and a permanent line on the launch
                            page.
                        </p>
                    </div>
                    <div className="ld-ask">
                        <span className="ld-ask-num">02</span>
                        <h4>Make one warm intro</h4>
                        <p>
                            One operator in your network whose Monday reviews you'd want
                            Scitus to fix. We'll take it from there — and we'll always tell
                            you how it went.
                        </p>
                    </div>
                    <div className="ld-ask">
                        <span className="ld-ask-num">03</span>
                        <h4>Tear this deck apart</h4>
                        <p>
                            Hit reply with the slide that didn't land, the claim that felt
                            soft, the line you'd cut. We'd rather rewrite it now than be
                            corrected by the internet on launch day.
                        </p>
                    </div>
                    <div className="ld-ask">
                        <span className="ld-ask-num">04</span>
                        <h4>Be an early believer</h4>
                        <p>
                            If you've been investing at this stage and our thesis resonates,
                            let's talk. We're not running a process. We're building a small
                            cap table of people who'd answer the phone on a hard day.
                        </p>
                    </div>
                </div>
            </Slide>

            {/* ─── Slide 18 — Close ───────────────────────────────────────────── */}
            <Slide id="18" full accent>
                <h2 className="ld-megaline ld-megaline-close">
                    One platform.<br />
                    One data layer.<br />
                    <span className="ld-accent">One source of truth.</span>
                </h2>
                <div className="ld-close-meta">
                    <a href="mailto:hello@scitus.ai" data-testid="close-email">
                        <Mail size={14} /> hello@scitus.ai
                    </a>
                    <a href="https://wa.me/919677031328?text=I'd%20like%20to%20talk%20about%20the%20Scitus%20launch" target="_blank" rel="noreferrer" data-testid="close-whatsapp">
                        <MessageCircle size={14} /> +91 96770 31328
                    </a>
                    <a href="https://scitus.ai" data-testid="close-site">
                        <ArrowRight size={14} /> scitus.ai
                    </a>
                </div>
                <p className="ld-close-sign">
                    Vyastics Technologies Pvt Ltd · Chennai · Austin<br />
                    © 2026 — Pre-launch · Please don't share publicly yet.
                </p>
            </Slide>
        </main>
    );
};

export default LaunchDeckPage;
